import React, { useEffect, useState } from 'react';
import { Building2, MapPin, Calendar } from 'lucide-react';
import { formatDate } from '../utils/Config';

const ExperienceItem = ({ title, company, from, to, location, description }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150;
  
  const shouldShowButton = description && description.length > maxLength;
  const displayText = isExpanded ? description : description?.slice(0, maxLength);

  return (
    <div className="border-b border-gray-200 pb-6 mb-6 last:border-0 last:mb-0 last:pb-0">
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="font-bold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2 text-gray-600 mt-1">
            <Building2 className="w-4 h-4" />
            <span className="font-medium">{company}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(from)} {to && <span>to {formatDate(to)}</span>}</span>
          </div>
          
          {location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>{location}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-3">
        {description ? (
          <div className="text-gray-600 text-sm">
            <p className="whitespace-pre-wrap break-words">
              {displayText}
              {shouldShowButton && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="ml-1 text-blue-600 hover:text-blue-800 font-medium transition-colors"
                >
                  {isExpanded ? " Show less" : "... Show more"}
                </button>
              )}
            </p>
          </div>
        ) : (
          <p className="text-gray-500 italic text-sm">No description available</p>
        )}
      </div>
    </div>
  );
};

const ExperienceSection = ({ user }) => {
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    setExperiences(user?.experience || []);
  }, [user]);

  if (!experiences?.length) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 w-full mx-auto mt-4">
        <h2 className="text-xl font-bold mb-4">Experience</h2>
        <p className="text-gray-500 italic">No experience information available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 w-full mx-auto mt-4">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Experience</h2>
      </div>

      <div className="divide-gray-200">
        {experiences.map((exp, index) => (
          <ExperienceItem
            key={`${exp.company}-${index}`}
            title={exp.title}
            company={exp.company}
            from={exp.from}
            to={exp.to}
            location={exp.location}
            description={exp.description}
          />
        ))}
      </div>
    </div>
  );
};

export default ExperienceSection;